import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useState } from "react";
import { ValidationResult } from "../../interfaces/doc";

const ValidationTabPanel: React.FC<{
  validations: Record<string, ValidationResult>;
  isHidden: boolean;
  key?: string;
}> = ({ validations, isHidden }) => (
  <div role="tabpanel" hidden={isHidden}>
    <Box sx={{ p: 3 }}>
      {Object.entries(validations).map(([messageKey, { message }]) => (
        <div
          key={`message-${messageKey}`}
          className="flex flex-col gap-0.5 items-start"
        >
          <Typography className="font-semibold text-sm text-[#4c4fa2]">
            {messageKey}
          </Typography>

          <Typography>{message}</Typography>
        </div>
      ))}
    </Box>
  </div>
);

const ValidationDisplay: React.FC<{
  validations: Record<string, ValidationResult>;
}> = ({ validations }) => {
  const getInitialTab = (validations: Record<string, ValidationResult>) => {
    const hasErrors = Object.values(validations).some(
      (v) => v.success === false
    );
    const hasNotApplicable = Object.values(validations).some(
      (v) => v.success === null
    );

    if (hasErrors)
      return 0; // Errors tab
    else if (hasNotApplicable)
      return 1; // Not applicable tab
    else return 2; // Validated tab
  };

  const [value, setValue] = useState(() => getInitialTab(validations));

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const errors = Object.fromEntries(
    Object.entries(validations).filter(
      ([_, validation]) => validation.success === false
    )
  );
  const validated = Object.fromEntries(
    Object.entries(validations).filter(
      ([_, validation]) => !!validation.success
    )
  );
  const notApplicable = Object.fromEntries(
    Object.entries(validations).filter(
      ([_, validation]) => validation.success === null
    )
  );

  const indicatorColor =
    value === 0 ? "error" : value === 1 ? "warning" : "success";

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          TabIndicatorProps={{
            sx: {
              backgroundColor: `var(--mui-palette-${indicatorColor}-main)`,
            },
          }}
        >
          <Tab
            label={
              <span
                style={
                  Object.keys(errors).length
                    ? { color: "var(--mui-palette-error-main)" }
                    : undefined
                }
              >
                Erreur ({Object.keys(errors).length})
              </span>
            }
            disabled={!Object.keys(errors).length}
          />
          <Tab
            label={
              <span
                style={
                  Object.keys(notApplicable).length
                    ? { color: "var(--mui-palette-warning-main)" }
                    : undefined
                }
              >
                Pas de résultat ({Object.keys(notApplicable).length})
              </span>
            }
            disabled={!Object.keys(notApplicable).length}
          />
          <Tab
            label={
              <span
                style={
                  Object.keys(validated).length
                    ? { color: "var(--mui-palette-success-main)" }
                    : undefined
                }
              >
                Valide ({Object.keys(validated).length})
              </span>
            }
            disabled={!Object.keys(validated).length}
          />
        </Tabs>
      </Box>
      <ValidationTabPanel validations={errors} isHidden={value !== 0} />
      <ValidationTabPanel validations={notApplicable} isHidden={value !== 1} />
      <ValidationTabPanel validations={validated} isHidden={value !== 2} />
    </>
  );
};

export default ValidationDisplay;
